import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Settings, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import type { RetrofitSurveyData } from '../utils/retrofitSurveyStorage';
import type { FloorPlanImageData } from '../services/retrofitPDFExportService';
import noorLogo from '../assets/NOOR.png';
import { useSubscription } from '../hooks/useSubscription';

interface RetrofitExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: { 
    logoDataUrl?: string; 
    headerColor?: string;
    companyName?: string;
    companyWebsite?: string;
    companyPhone?: string;
    companyEmail?: string;
    companyAddress?: string;
    includePhotos: boolean; 
    includeFloorPlan: boolean; 
    floorPlanImages: FloorPlanImageData[];
    format?: 'pdf' | 'html' | 'both';
    onProgress?: (progress: number, stage: string) => void; // Real-time progress callback
  }) => Promise<void>;
  surveyData: RetrofitSurveyData;
}

// Profile management interface
interface CustomizationProfile {
  id: string;
  name: string;
  logoDataUrl: string | null;
  headerColor: string;
  companyName: string;
  companyWebsite: string;
  companyPhone: string;
  companyEmail: string;
  companyAddress: string;
  createdAt: number;
}

export default function RetrofitExportModal({ isOpen, onClose, onExport, surveyData }: RetrofitExportModalProps) {
  // Check subscription status
  const { isStandardPlan } = useSubscription();
  
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStage, setExportStage] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = useState<boolean>(false);
  // Always include photos and floor plans - no user choice needed
  const includePhotos = true;
  const includeFloorPlan = true;
  
  // Customization state
  const [headerColor, setHeaderColor] = useState<string>('#f59e0b');
  const [companyName, setCompanyName] = useState<string>('Energy Ltd');
  const [companyWebsite, setCompanyWebsite] = useState<string>('www.example.com');
  const [companyPhone, setCompanyPhone] = useState<string>('0800 123 4567');
  const [companyEmail, setCompanyEmail] = useState<string>('info@example.com');
  const [companyAddress, setCompanyAddress] = useState<string>('123 Business Street, London, EC2A 4DP');
  
  // Step navigation
  const [currentStep, setCurrentStep] = useState<'customization' | 'format'>('customization');
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'html' | 'both'>('html');
  
  // Color picker state
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [tempColor, setTempColor] = useState(headerColor);
  
  // Profile management
  const [savedProfiles, setSavedProfiles] = useState<CustomizationProfile[]>([]);
  const [profileName, setProfileName] = useState<string>('');
  const [showSaveProfile, setShowSaveProfile] = useState(false);
  
  // Recent logos state
  const [recentLogos, setRecentLogos] = useState<Array<{url: string, name: string}>>([]);

  // Load saved profiles from localStorage
  useEffect(() => {
    const loadedProfiles = localStorage.getItem('retrofit-export-profiles');
    if (loadedProfiles) {
      try {
        setSavedProfiles(JSON.parse(loadedProfiles));
      } catch (error) {
        console.error('Failed to load profiles:', error);
      }
    }
  }, []);

  // Load recent logos from localStorage
  useEffect(() => {
    const savedLogos = localStorage.getItem('recentLogos');
    if (savedLogos) {
      try {
        setRecentLogos(JSON.parse(savedLogos));
      } catch (error) {
        console.error('Failed to load recent logos:', error);
      }
    }
  }, []);

  // Load default logo on mount
  useEffect(() => {
    const loadDefaultLogo = async () => {
      try {
        const response = await fetch(noorLogo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setLogoPreview(e.target.result as string);
          }
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.log('Could not load default logo:', error);
      }
    };
    
    if (isOpen && !logoPreview && !logoRemoved) {
      loadDefaultLogo();
    }
  }, [isOpen, logoPreview, logoRemoved]);

  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportStage('Initializing export...');
    
    // Standard plan users: NOOR logo only, no contact info
    const exportCompanyName = isStandardPlan ? '' : companyName;
    const exportCompanyWebsite = isStandardPlan ? '' : companyWebsite;
    const exportCompanyPhone = isStandardPlan ? '' : companyPhone;
    const exportCompanyEmail = isStandardPlan ? '' : companyEmail;
    const exportCompanyAddress = isStandardPlan ? '' : companyAddress;
    
    try {
      // Actually perform the export with real-time progress tracking
      await onExport({
        logoDataUrl: logoPreview || undefined,
        headerColor,
        companyName: exportCompanyName,
        companyWebsite: exportCompanyWebsite,
        companyPhone: exportCompanyPhone,
        companyEmail: exportCompanyEmail,
        companyAddress: exportCompanyAddress,
        includePhotos: true,
        includeFloorPlan: true,
        floorPlanImages: [],
        format: selectedFormat,
        // Real-time progress callback - unique to each file!
        onProgress: (progress: number, stage: string) => {
          setExportProgress(Math.round(progress));
          setExportStage(stage);
        }
      });
      
      // Brief pause to show completion
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      
      // Check if error is due to base64 photos not being uploaded
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Base64 photo data detected') || errorMessage.includes('uploaded to CDN')) {
        alert(
          '⏳ Photo Upload in Progress\n\n' +
          'Some photos are still being uploaded to the cloud. ' +
          'Please wait a moment and try exporting again.\n\n' +
          'Tip: Wait for all photo uploads to complete before exporting. ' +
          'You can check if uploads are complete by refreshing the page - ' +
          'if photos still display correctly, uploads are done.'
        );
      } else {
        alert('Export failed. Please try again.\n\nError: ' + errorMessage);
      }
    } finally {
      setIsExporting(false);
      setExportProgress(0);
      setExportStage('');
    }
  };

  const goToFormat = () => {
    setCurrentStep('format');
  };

  const goBackToCustomization = () => {
    setCurrentStep('customization');
  };

  const openColorPicker = () => {
    setTempColor(headerColor);
    setShowColorPicker(true);
  };

  const handleColorChange = (newColor: string) => {
    setTempColor(newColor);
  };

  const applyColor = () => {
    setHeaderColor(tempColor);
    setShowColorPicker(false);
  };

  const cancelColorChange = () => {
    setTempColor(headerColor);
    setShowColorPicker(false);
  };

  const saveProfile = () => {
    if (!profileName.trim()) {
      alert('Please enter a profile name');
      return;
    }
    
    const newProfile: CustomizationProfile = {
      id: Date.now().toString(),
      name: profileName.trim(),
      logoDataUrl: logoPreview,
      headerColor,
      companyName,
      companyWebsite,
      companyPhone,
      companyEmail,
      companyAddress,
      createdAt: Date.now()
    };
    
    const updatedProfiles = [...savedProfiles, newProfile];
    setSavedProfiles(updatedProfiles);
    localStorage.setItem('retrofit-export-profiles', JSON.stringify(updatedProfiles));
    setProfileName('');
    setShowSaveProfile(false);
  };
  
  const loadProfile = (profile: CustomizationProfile) => {
    setLogoPreview(profile.logoDataUrl);
    setHeaderColor(profile.headerColor);
    setCompanyName(profile.companyName);
    setCompanyWebsite(profile.companyWebsite);
    setCompanyPhone(profile.companyPhone);
    setCompanyEmail(profile.companyEmail || 'info@example.com');
    setCompanyAddress(profile.companyAddress || '123 Business Street, London, EC2A 4DP');
    
    // Clear the file input since we're loading from saved data
    setLogoFile(null);
    const fileInput = document.getElementById('retrofit-logo-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };
  
  const deleteProfile = (profileId: string) => {
    if (!confirm('Are you sure you want to delete this profile?')) return;
    
    const updatedProfiles = savedProfiles.filter(p => p.id !== profileId);
    setSavedProfiles(updatedProfiles);
    localStorage.setItem('retrofit-export-profiles', JSON.stringify(updatedProfiles));
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          const logoDataUrl = e.target.result as string;
          setLogoPreview(logoDataUrl);
          setLogoFile(file);
          setLogoRemoved(false);
          saveRecentLogo(logoDataUrl, file.name);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const saveRecentLogo = (logoUrl: string, fileName: string) => {
    const newLogo = { url: logoUrl, name: fileName };
    const updatedLogos = [newLogo, ...recentLogos.filter(logo => logo.url !== logoUrl)].slice(0, 6);
    setRecentLogos(updatedLogos);
    localStorage.setItem('recentLogos', JSON.stringify(updatedLogos));
  };

  const selectRecentLogo = (logoData: {url: string, name: string}) => {
    setLogoPreview(logoData.url);
    setLogoFile(null);
  };

  const removeRecentLogo = (logoUrl: string) => {
    const updatedLogos = recentLogos.filter(logo => logo.url !== logoUrl);
    setRecentLogos(updatedLogos);
    localStorage.setItem('recentLogos', JSON.stringify(updatedLogos));
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoRemoved(true);
    const fileInput = document.getElementById('retrofit-logo-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className={`bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto border border-white/20 transition-all duration-300 ${
        currentStep === 'format' ? 'max-w-md' : 'max-w-lg'
      }`}>
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          {/* Background with large "Retrofit Survey" text */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
            <span className="text-[120px] font-black text-white/5 whitespace-nowrap select-none">
              RETROFIT SURVEY
            </span>
          </div>
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 p-6 relative">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <h2 className="text-xl font-bold text-white mb-1 drop-shadow-lg">Export Retrofit Survey</h2>
                <p className="text-blue-100 text-sm drop-shadow">Generate comprehensive PDF report</p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
          
          {/* Only show customization fields on customization step */}
          {currentStep === 'customization' && isStandardPlan && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-amber-800">
                <Settings className="w-4 h-4" />
                <p className="text-sm font-medium">Customization is not available on the Standard plan</p>
              </div>
              <p className="text-xs text-amber-700 mt-2">Upgrade to Pro to customize logos, colors, and company details in your exports.</p>
            </div>
          )}
          
          {currentStep === 'customization' && !isStandardPlan && (
            <>
          {/* Survey Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">Survey Information</h3>
                <div className="space-y-1 text-xs text-blue-700">
                  <div><strong>Property:</strong> {surveyData.initial_questionnaire?.streetAddress || surveyData.initial_questionnaire?.postCode || 'Not specified'}</div>
                  <div><strong>Rooms:</strong> {surveyData.room_details?.rooms?.length || 0} rooms surveyed</div>
                  {surveyData.initial_questionnaire?.photos && Object.values(surveyData.initial_questionnaire.photos).some(section => section.photos?.length > 0) && (
                    <div><strong>Photos:</strong> {Object.values(surveyData.initial_questionnaire.photos).reduce((sum, section) => sum + (section.photos?.length || 0), 0)} captured</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Customization Options */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center space-x-2">
                <Settings className="w-4 h-4 text-gray-600" />
                <h3 className="text-sm font-semibold text-gray-900">Customization</h3>
                {logoFile && (
                  <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">Custom</span>
                )}
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Saved Profiles */}
              {savedProfiles.length > 0 && (
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-700">Saved Profiles</label>
                    <span className="text-[10px] text-gray-500">{savedProfiles.length} saved</span>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {savedProfiles.map(profile => (
                      <div key={profile.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                        {profile.logoDataUrl && (
                          <img src={profile.logoDataUrl} alt="" className="h-6 w-auto object-contain" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-900 truncate">{profile.name}</div>
                          <div className="flex items-center gap-2 text-[10px] text-gray-500">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: profile.headerColor }}></div>
                            <span className="truncate">{profile.companyName}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => loadProfile(profile)}
                          className="px-2 py-1 text-[10px] font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => deleteProfile(profile.id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Save Current Profile */}
              {!showSaveProfile ? (
                <button
                  onClick={() => setShowSaveProfile(true)}
                  className="w-full px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Current Configuration as Profile
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Enter profile name..."
                    className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && saveProfile()}
                    autoFocus
                  />
                  <button
                    onClick={saveProfile}
                    className="px-3 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setShowSaveProfile(false); setProfileName(''); }}
                    className="px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
              
              {/* Logo Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">Logo</label>
                  {logoFile && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">Custom</span>
                  )}
                </div>
                
                {logoPreview ? (
                  <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded border">
                    <img src={logoPreview} alt="Logo" className="h-8 w-auto object-contain" />
                    <div className="flex-1 text-xs text-gray-500 truncate">
                      {logoFile ? logoFile.name : 'NOOR logo'}
                    </div>
                    <button
                      onClick={handleRemoveLogo}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove logo"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      id="retrofit-logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="retrofit-logo-upload"
                      className="cursor-pointer text-xs text-gray-500 hover:text-gray-700"
                    >
                      Click to upload logo
                    </label>
                  </div>
                )}
              </div>

              {/* Recent Logos */}
              {recentLogos.length > 0 && (
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-600">Recent Logos</label>
                    <span className="text-xs text-gray-500">{recentLogos.length} saved</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {recentLogos.map((logo, index) => (
                      <div key={index} className="relative group">
                        <button
                          onClick={() => selectRecentLogo(logo)}
                          className="w-full p-2 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 hover:border-gray-300 transition-colors"
                          title={logo.name}
                        >
                          <img 
                            src={logo.url} 
                            alt={logo.name}
                            className="h-6 w-auto mx-auto object-contain"
                          />
                          <div className="text-xs text-gray-500 mt-1 truncate">
                            {logo.name}
                          </div>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRecentLogo(logo.url);
                          }}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                          title="Remove logo"
                        >
                          <X size={8} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Theme Color Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">Theme Color</label>
                  {headerColor !== '#f59e0b' && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">Custom</span>
                  )}
                </div>
                
                <div className="space-y-2">
                  <button
                    onClick={openColorPicker}
                    className="flex items-center space-x-2 w-full px-3 py-2 text-left border border-gray-300 rounded hover:border-gray-400 transition-colors"
                  >
                    <div 
                      className="w-5 h-5 rounded border border-gray-300 shadow-inner"
                      style={{ backgroundColor: headerColor }}
                    ></div>
                    <span className="text-sm font-mono">{headerColor}</span>
                    <span className="text-xs text-gray-500 ml-auto">Click to change</span>
                  </button>
                  <div className="text-[10px] text-gray-500 pl-1">
                    Applied to headers, borders & accent elements
                  </div>
                </div>

                {/* Color Picker Modal */}
                {showColorPicker && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
                      <h3 className="text-lg font-semibold mb-4">Choose Color</h3>
                      
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            value={tempColor}
                            onChange={(e) => handleColorChange(e.target.value)}
                            className="w-12 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                          />
                          <div className="flex-1">
                            <input
                              type="text"
                              value={tempColor}
                              onChange={(e) => handleColorChange(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                              placeholder="#f59e0b"
                            />
                          </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3 pt-4 border-t">
                          <button
                            onClick={cancelColorChange}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={applyColor}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Company Details */}
              <div className="space-y-3 pt-3 border-t border-gray-100">
                <div className="text-xs font-medium text-gray-700 mb-2">Company Details</div>
                
                <div>
                  <label className="text-[10px] text-gray-600 mb-1 block">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Energy Ltd"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="text-[10px] text-gray-600 mb-1 block">Website</label>
                  <input
                    type="text"
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    placeholder="www.yourcompany.com"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="text-[10px] text-gray-600 mb-1 block">Phone Number</label>
                  <input
                    type="text"
                    value={companyPhone}
                    onChange={(e) => setCompanyPhone(e.target.value)}
                    placeholder="0800 123 4567"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="text-[10px] text-gray-600 mb-1 block">Email Address</label>
                  <input
                    type="email"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    placeholder="info@yourcompany.com"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="text-[10px] text-gray-600 mb-1 block">Address</label>
                  <input
                    type="text"
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder="123 Business Street, City, Postcode"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

            </div>
          </div>
            </>
          )}

          {/* Step Navigation */}
          {currentStep === 'customization' ? (
            <button
              onClick={goToFormat}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg"
            >
              <span>Continue to Export Format</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="space-y-4">
              {/* Format Selection */}
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Choose Export Format</h3>
                <p className="text-xs text-gray-600 mb-4">Select the format you'd like to export:</p>

                {/* Format Options - Compact */}
                <div className="space-y-2">
                  {/* PDF */}
                  <button
                    onClick={() => setSelectedFormat('pdf')}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                      selectedFormat === 'pdf'
                        ? 'bg-red-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <FileText className="w-4 h-4" />
                      <span>PDF</span>
                    </div>
                  </button>

                  {/* HTML - Recommended */}
                  <button
                    onClick={() => setSelectedFormat('html')}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                      selectedFormat === 'html'
                        ? 'bg-green-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>🌐</span>
                      <span>HTML (Recommended)</span>
                    </div>
                  </button>

                  {/* Both */}
                  <button
                    onClick={() => setSelectedFormat('both')}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                      selectedFormat === 'both'
                        ? 'bg-purple-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>⭐</span>
                      <span>Both Formats</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={goBackToCustomization}
                  disabled={isExporting}
                  className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="flex-[2] py-2.5 px-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg text-sm"
                >
                  {isExporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Generate</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Progress Modal Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900/95 via-slate-800/90 to-slate-900/95 backdrop-blur-md flex items-center justify-center z-[10000]">
          <div className="bg-gradient-to-br from-white via-slate-50 to-white rounded-3xl shadow-2xl p-8 sm:p-12 max-w-md w-full mx-4 border border-slate-200/60">
            {/* Icon Animation */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                {/* Outer rotating ring */}
                <div className="absolute inset-0 rounded-full border-4 border-blue-200 animate-spin" style={{ animationDuration: '3s' }}></div>
                {/* Inner pulsing circle */}
                <div className="relative bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-6 shadow-lg animate-pulse">
                  <Download className="w-12 h-12 text-white drop-shadow-lg" />
                </div>
              </div>
            </div>
            
            {/* Progress Title */}
            <h3 className="text-2xl font-bold text-center bg-gradient-to-r from-slate-800 via-slate-700 to-slate-900 bg-clip-text text-transparent mb-2">
              Generating Your Report
            </h3>
            
            {/* Current Stage */}
            <p className="text-center text-slate-600 font-medium mb-6">
              {exportStage}
            </p>
            
            {/* Progress Bar Container */}
            <div className="relative mb-4">
              <div className="h-3 bg-gradient-to-r from-slate-200 to-slate-100 rounded-full overflow-hidden shadow-inner">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                  style={{ width: `${exportProgress}%` }}
                >
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                </div>
              </div>
            </div>
            
            {/* Percentage Display */}
            <div className="text-center">
              <span className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {exportProgress}%
              </span>
            </div>
            
            {/* Helper Text */}
            <p className="text-center text-xs text-slate-500 mt-6">
              Please wait while we prepare your comprehensive retrofit survey report...
            </p>
          </div>
        </div>
      )}
      
      {/* Inline style for shimmer animation */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .animate-shimmer {
            animation: shimmer 2s infinite;
          }
        `
      }} />
    </div>
  );
}
